import { BaseFreeGift } from '@/stores/promotion';
import {
  BasePromotion,
  BasePromotionRewardProductItem,
  FreeGiftDisplay,
  FreeGiftItemDisplay,
  FreeGiftProductGroupDisplay,
} from '@/types/http/promotion/common';

type FreeGiftItem = BasePromotionRewardProductItem & { item_id: string; qty: number };

export type FreeGiftGroup = {
  promotion_id: string;
  product_group: {
    product_group_id: string;
    product_name: string;
    slug: string;
    stock: number;
  };
  items: FreeGiftItem[];
};

export type PromoGiftCaps = {
  globalMax: number;
  groupCaps: Record<string, number>;
};

type CartLine = { item_id: string; qty: number };
type GiftKey = string;
type ErrorInfo = { promotionId?: string; text?: string };

/**
 * Clamps a number within the inclusive range specified by `min` and `max`.
 *
 * @param n - The number to clamp.
 * @param min - The lower bound of the range (default is 0).
 * @param max - The upper bound of the range (default is 100).
 * @returns The clamped value, which will be no less than `min` and no greater than `max`.
 */
const clamp = (n: number, min = 0, max = 100) => Math.min(Math.max(n, min), max);

/**
 * Converts the given value to a number.
 *
 * If the value is not a number (or a string that can be converted to a number), the given fallback value is returned.
 *
 * @param {unknown} v - The value to convert to a number.
 * @param {number} [fallback=0] - The value to return if the conversion fails.
 * @return {number} The given value converted to a number, or the fallback value if the conversion fails.
 */
export const toNum = (v: unknown, fallback = 0) =>
  Number.isFinite(Number(v)) ? Number(v) : fallback;

/**
 * Calculates the percentage of the grand total relative to a minimum value.
 * If the minimum is not provided or is less than or equal to zero, returns 100.
 * The result is clamped to ensure it stays within a valid range.
 *
 * @param grandTotal - The total amount to compare.
 * @param minimum - The minimum value to use as the denominator.
 * @returns The percentage (as a number) of grandTotal relative to minimum, clamped appropriately.
 */
export const calculatePercentage = (grandTotal: number, minimum: number) => {
  if (!minimum || minimum <= 0) return 100;
  return clamp((grandTotal / minimum) * 100);
};

/**
 * Determines whether a promotion candidate should be disabled for selection based on current selection and eligibility rules.
 *
 * @param candidate - The promotion being considered for selection.
 * @param selected - Array of promotion IDs that are currently selected.
 * @param allPromos - List of all available promotions.
 * @param grandTotal - The current grand total amount in the cart.
 * @returns `true` if the candidate promotion should be disabled (cannot be selected), otherwise `false`.
 *
 * @remarks
 * - Already selected promotions can always be deselected (never disabled).
 * - Promotions are disabled if the cart total is below their minimum spending requirement.
 * - Non-stackable (non-overlap) promotions cannot be selected if any promotion is already selected.
 * - Stackable (overlap) promotions cannot be selected if a non-stackable promotion is already selected.
 */
export const computeDisabled = (
  candidate: BasePromotion,
  selected: string[],
  allPromos: BasePromotion[],
  grandTotal: number
) => {
  // already selected can always be deselected
  if (selected.includes(candidate.promotion_id)) return false;
  // not eligible by minimum spend
  if (grandTotal < candidate.minimum_spending) return true;

  const selectedPromos = allPromos.filter((p) => selected.includes(p.promotion_id));
  const hasNonStackable = selectedPromos.some((p) => !p.is_overlap);

  // rules:
  // - non-overlap (non-stackable) cannot be selected if ANY already selected
  if (!candidate.is_overlap && selectedPromos.length > 0) return true;
  // - overlap (stackable) cannot be selected if there is a non-stackable already
  if (candidate.is_overlap && hasNonStackable) return true;

  return false;
};

export const computeCountedMultiplySpecific = (
  promo: BasePromotion,
  cartItems?: CartLine[]
): number => {
  if (!cartItems?.length) return 0;

  const spec = promo.promotion_specific?.[0];
  if (!spec) return 0;

  const requiredQty = toNum(spec.qty, 0);
  if (requiredQty <= 0) return 0;

  const eligibleIds = new Set((spec.item_promotion_specific ?? []).map((x: any) => x.item_id));
  const totalBought = cartItems.reduce(
    (sum, c) => sum + (eligibleIds.has(c.item_id) ? c.qty : 0),
    0
  );

  if (totalBought < requiredQty) return 0;

  const raw = Math.floor(totalBought / requiredQty);
  const max = toNum(promo.max_multiply, Infinity);

  return Math.min(raw, max || Infinity);
};

/**
 * Kuota free gift untuk SATU promo.
 * - globalMax = sum(qty semua reward) * counted_multiply
 * - groupCaps = qty tiap product_group * counted_multiply
 *  @param promo - BasePromotion object containing the promotion details.
 */
export const getPromoGiftCaps = (promo: BasePromotion): PromoGiftCaps => {
  const rewards = promo.product_reward ?? [];
  const multi = Math.max(Number(promo.counted_multiply) || 1, 1);

  const groupCaps: Record<string, number> = {};
  let globalMax = 0;

  for (const reward of rewards) {
    const gid = reward.reward_items;
    if (!gid.length) continue;

    const base = Math.max(Number(reward.qty) || 0, 0);
    const cap = base * multi;

    for (const item of gid) {
      const groupId = item.target_id;
      if (!groupId) continue;

      groupCaps[groupId] = (groupCaps[groupId] ?? 0) + cap;
    }

    globalMax += cap;
  }

  if (rewards.length > 0 && globalMax === 0) {
    globalMax = 1;
    for (const groupId of Object.keys(groupCaps)) {
      if (groupCaps[groupId] == null) groupCaps[groupId] = 1;
    }
  }

  return { globalMax, groupCaps };
};

/**
 * Calculates the maximum quantity for a given promotion.
 *
 * @param {BasePromotion} promo - The promotion object.
 * @return {number} The maximum quantity for the promotion, or 0 if base or mult is falsy.
 */
export const getMaxQtyForPromo = (promo: BasePromotion) => {
  const rewards = promo.product_reward;

  const sumQty = rewards?.reduce((acc, r) => acc + (Number(r?.qty) || 0), 0) ?? 0;
  const baseQty =
    sumQty > 0
      ? sumQty
      : rewards?.[0]?.qty && Number(rewards?.[0]?.qty) > 0
      ? Number(rewards?.[0]?.qty)
      : rewards?.length && rewards.length > 0
      ? 1
      : 0;

  const cm = (promo as any).counted_multiply;
  const multiplier = typeof cm === 'number' ? Math.max(cm, 1) : cm ? 1 : 1;

  return baseQty * multiplier;
};

/**
 * Determines if the given promotion is eligible for a free gift.
 *
 * A promotion is eligible for a free gift if it has a valid `product_reward` array and it is not empty.
 *
 * @param {BasePromotion} promo - The promotion to check for free gift eligibility.
 * @return {boolean} True if the promotion is eligible for a free gift, false otherwise.
 */
export const isPromoEligibleFreeGift = (promo: BasePromotion): boolean => {
  // Check if the promotion has a valid `product_reward` array and it is not empty.
  return Array.isArray(promo.product_reward) && promo.product_reward.length > 0;
};

/**
 * Calculates the sum of selected quantities for each item within each gift in the given array of `BaseFreeGift` objects.
 *
 * @param {BaseFreeGift[]} gifts - An array of `BaseFreeGift` objects.
 * @return {number} The sum of selected quantities for each item within each gift in the given array.
 */
export const sumSelectedQtyForPromo = (gifts: BaseFreeGift[]) =>
  gifts.reduce((sum, g) => sum + g.items.reduce((s, it) => s + (it.qty ?? 0), 0), 0);

export const getEffectiveMultiply = (promo: BasePromotion) => {
  const counted = Math.max(0, toNum(promo.counted_multiply ?? 0, 0));
  const base = counted > 0 ? counted : 1;
  const maxMul = Math.max(0, toNum(promo.max_multiply ?? 0, 0));
  return maxMul > 0 ? Math.min(base, maxMul) : base;
};

/**
 * Detects if there is a conflict between existing promotions and new promotions.
 *
 * A conflict can occur if there are promotions that have non-overlapping items and the new promotions also have non-overlapping items.
 * Or if all existing promotions have non-overlapping items, but the new promotions have overlapping items.
 *
 * @param {BasePromotion[]} existingPromos - The existing promotions.
 * @param {BasePromotion[]} newPromos - The new promotions.
 * @return {boolean} True if there is a conflict, false otherwise.
 */
export const detectConflictPromotion = (
  existingPromos: BasePromotion[],
  newPromos: BasePromotion[]
): boolean => {
  if (!existingPromos?.length || !newPromos?.length) return false;
  return existingPromos.some((p) => !p.is_overlap) || newPromos.some((p) => !p.is_overlap);
};

/**
 * Ensures that the given `promo` has its `applied_product_reward` set based on the given `sources` array.
 * If the `promo` already has a valid `applied_product_reward` array, it will be returned as is.
 * If the `promo` does not have a valid `applied_product_reward` array and there is a matching `BasePromotion` object in the `sources` array,
 * it will return a new `BasePromotion` object with the `applied_product_reward` set based on the matching object in the `sources` array.
 * If there is no matching object in the `sources` array, it will return the original `promo` object.
 */
export const ensureApplied = (promo: BasePromotion, sources: BasePromotion[]): BasePromotion => {
  if ((promo.applied_product_reward?.length ?? 0) > 0) return promo;
  const src = sources.find((s) => s.promotion_id === promo.promotion_id);
  if (src && (src.applied_product_reward?.length ?? 0) > 0) {
    return { ...promo, applied_product_reward: src.applied_product_reward };
  }
  return promo;
};

/**
 * Builds the initial free gift group(s) for a given promotion.
 *
 * Determines the quantity of free gifts based on reward configuration and multiplier; returns
 * an array of groups, or an empty array if no valid include reward or quantity is zero.
 *
 * @param promo - The base promotion object containing reward and applied product information.
 * @returns An array of `FreeGiftDisplay` objects (empty when no valid gifts).
 */
export const buildAutoFreeGifts = (promo: BasePromotion): FreeGiftDisplay[] => {
  const include = promo.product_reward?.find(
    (r) => r.reward_type === 'product' && r.rule_type === 'include'
  );

  if (!include) return [];

  const includeQty = Math.max(0, include?.qty ?? 0);
  if (includeQty <= 0) return [];

  const multiply = getEffectiveMultiply(promo);
  const qty = includeQty * multiply;

  const includeItems = include.reward_items ?? [];
  const isAllProduct = includeItems.some(
    (it) => typeof it.item_name === 'string' && /all\s*product/i.test(it.item_name)
  );

  const includeGroupIds = includeItems
    .filter(
      (it) =>
        (it.item_type ?? '').toLowerCase() === 'product_group' ||
        (it.item_type ?? '').toLowerCase() === 'group'
    )
    .map((it) => it.target_id);

  const groups = new Map<
    string,
    { group: FreeGiftProductGroupDisplay; items: FreeGiftItemDisplay[] }
  >();

  for (const ap of promo.applied_product_reward ?? []) {
    // Skip if item_id is missing to satisfy type requirement (narrows to string below)
    if (!ap.item_id) continue;

    const apGroupId = ap.product_group_id ?? '';
    const resolvedGroupId = isAllProduct
      ? apGroupId || 'default'
      : includeGroupIds.length > 0
      ? apGroupId && includeGroupIds.includes(apGroupId)
        ? apGroupId
        : includeGroupIds[0]
      : apGroupId || 'default';

    const product_group: FreeGiftProductGroupDisplay = {
      product_group_id: resolvedGroupId,
      product_name: ap.product_name,
      slug: ap.slug,
      sell_price: ap.sell_price,
      product_assets: ap.product_assets,
      stock: ap.stock,
      total_variants: ap.total_variants,
      is_variants: ap.is_variants,
    };

    const item: FreeGiftItemDisplay = {
      // id varian/item
      item_id: ap.item_id,
      item_name: ap.item_name,
      // kompat filter lama + tampilan
      product_group_id: ap.product_group_id,
      product_name: ap.product_name,
      slug: ap.slug,
      sell_price: ap.sell_price,
      product_assets: ap.product_assets,
      stock: ap.stock,
      is_variants: ap.is_variants,
      total_variants: ap.total_variants,
      // qty final (sudah * multiplier dan clamp)
      qty,
    };

    const pack = groups.get(resolvedGroupId);
    if (pack) {
      const idx = pack.items.findIndex((it) => it.item_id === item.item_id);
      if (idx >= 0) {
        pack.items[idx].qty = item.qty;
      } else {
        pack.items.push(item);
      }
    } else {
      groups.set(resolvedGroupId, { group: product_group, items: [item] });
    }
  }

  return Array.from(groups.values()).map((v) => ({
    promotion_id: promo.promotion_id,
    product_group: v.group,
    items: v.items,
  }));
};

export const mergeFreeGiftsAppend = (
  existing: BaseFreeGift[] = [],
  incoming: BaseFreeGift[] = []
): BaseFreeGift[] => {
  const itemKey = (it: BaseFreeGift['items'][number]): GiftKey =>
    `${it.item_id}::${(it as any).variant_id ?? ''}`;

  const groupKey = (g: BaseFreeGift) =>
    `${g.promotion_id ?? ''}::${g.product_group.product_group_id}`;

  const map = new Map<string, BaseFreeGift>();

  for (const g of existing) {
    map.set(groupKey(g), {
      ...g,
      items: g.items.map((it) => ({ ...it, qty: it.qty ?? 1 })),
    });
  }

  for (const g of incoming) {
    const k = groupKey(g);
    const prev = map.get(k);
    if (!prev) {
      map.set(k, {
        ...g,
        items: g.items.map((it) => ({ ...it, qty: it.qty ?? 1 })),
      });
      continue;
    }

    const items = new Map<GiftKey, BaseFreeGift['items'][number]>();

    for (const it of prev.items) {
      items.set(itemKey(it), { ...it, qty: it.qty ?? 1 });
    }

    for (const it of g.items) {
      const key = itemKey(it);
      const ex = items.get(key);
      if (!ex) {
        items.set(key, { ...it, qty: it.qty ?? 1 });
      } else {
        const nextQty = (ex.qty ?? 1) + (it.qty ?? 1);
        items.set(key, { ...ex, qty: nextQty });
      }
    }
    map.set(k, { ...prev, items: Array.from(items.values()) });
  }

  return Array.from(map.values());
};

function tryParseJSON<T = unknown>(input: unknown): T | unknown {
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as T;
  } catch {
    return input;
  }
}

export function extractErrorInfo(payload: unknown): ErrorInfo {
  // string? coba parse dulu
  if (typeof payload === 'string') {
    const reparsed = tryParseJSON(payload);
    if (reparsed !== payload) return extractErrorInfo(reparsed);
    // string polos, coba regex promotion_id
    const m =
      payload.match(/promotion[_\s-]?id["']?\s*[:=]\s*["']?([A-Za-z0-9-]+)/i) ||
      payload.match(/promo[_\s-]?id["']?\s*[:=]\s*["']?([A-Za-z0-9-]+)/i);
    return { promotionId: m?.[1], text: payload };
  }

  // array? cek satu-satu
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const info = extractErrorInfo(item);
      if (info.promotionId || info.text) return info;
    }
    return {};
  }

  // object?
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;

    if (typeof obj.promotion_id === 'string') {
      const text =
        (typeof obj.message === 'string' && obj.message) ||
        (typeof obj.detail === 'string' && obj.detail) ||
        undefined;
      return { promotionId: obj.promotion_id, text };
    }

    const candidates: unknown[] = [];
    if (Array.isArray(obj.data)) candidates.push(obj.data);
    if (Array.isArray(obj.errors)) candidates.push(obj.errors);
    if (Array.isArray(obj.error)) candidates.push(obj.error);

    for (const c of candidates) {
      const info = extractErrorInfo(c);
      if (info.promotionId || info.text) {
        if (!info.text && typeof obj.message === 'string') {
          info.text = obj.message;
        }
        return info;
      }
    }

    // kalau ga ada nested array yang relevan, ambil text dari field umum
    const text =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.error === 'string' && obj.error) ||
      (typeof obj.detail === 'string' && obj.detail) ||
      undefined;

    return { text };
  }

  return {};
}
